import { useState } from 'react';
import type { MediaItem } from '@/types';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchStorageAsset } from '@/features/search/searchSlice';
import { setPlaying, updateCurrentTime, requestAutoplay, setClipEndBoundary } from '@/features/player/playerSlice';
import { toggleTileSelection, setTranscriptSearch, openDetailDrawer } from '@/features/ui/uiSlice';
import { openAssignModal } from '@/features/sections/sectionsSlice';
import { useHoverVideoPreview } from '@/hooks/useHoverVideoPreview';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import demoVideo from '@/assets/videos/Image_from_iOS.MOV';
import styles from './MediaTile.module.css';

interface MediaTileProps {
  item: MediaItem;
  variant?: 'grid';
  onRemove?: () => void;
  /** Permanently delete the asset (distinct from onRemove, which only unpins
   * it from a collection). Renders a delete button on hover. */
  onDelete?: () => void;
  /** Overrides the default open-drawer behavior (used by search results,
   * which need to seek to the matched moment/clip before opening). */
  onOpen?: () => void;
}

export default function MediaTile({ item, variant, onRemove, onDelete, onOpen }: MediaTileProps) {
  const dispatch = useAppDispatch();
  const { activeAsset, loadingAssetId } = useAppSelector((s) => s.search);
  const selectedIds = useAppSelector((s) => s.ui.selectedTileIds);
  const isSelected = selectedIds.includes(item.id);
  const hasSelection = selectedIds.length > 0;

  const [isPortrait, setIsPortrait] = useState(false);

  // Image assets have no video stream to preview on hover
  const { videoRef, handleMouseEnter, handleMouseLeave } = useHoverVideoPreview(
    item.contentType === 'image' ? undefined : item.storage_url
  );

  const isImage = item.contentType === 'image';
  const isLoading = loadingAssetId === item.id;
  const isActive = activeAsset?.asset_id === item.id && !loadingAssetId;
  // Search result tiles (identified by the onOpen override) don't support
  // multi-select or the quick-play/details/add-to-collection hover actions.
  const isSearchResult = !!onOpen;

  const playVideo = () => {
    if (onOpen) { onOpen(); return; }
    trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_PLAY, { assetId: item.id });
    dispatch(setPlaying(false));
    dispatch(updateCurrentTime(0));
    dispatch(requestAutoplay());
    dispatch(setTranscriptSearch(''));
    dispatch(fetchStorageAsset(item.id));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openDetails = () => {
    if (onOpen) { onOpen(); return; }
    trackEvent(ANALYTICS_EVENTS.DRAWER_OPEN, { assetId: item.id, source: 'media_tile' });
    // Pause/reset the shared player state — the drawer's own video reuses it
    // (so transcript "click to jump" works there too), and the main dashboard
    // no longer shows a hero player that would otherwise keep playing.
    dispatch(setPlaying(false));
    dispatch(updateCurrentTime(0));
    dispatch(setClipEndBoundary(null));
    dispatch(fetchStorageAsset(item.id));
    dispatch(openDetailDrawer(item));
  };

  const handleTileClick = () => {
    trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_CLICK, { assetId: item.id });
    if (hasSelection && !isSearchResult) {
      dispatch(toggleTileSelection(item.id));
    } else {
      openDetails();
    }
  };

  const handleCheckbox = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_SELECT, { assetId: item.id, selected: !isSelected });
    dispatch(toggleTileSelection(item.id));
  };

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    playVideo();
  };

  const badgeClass =
    item.badge === 'new' ? styles.badgeNew : item.badge === 'hot' ? styles.badgeHot : '';
  const badgeLabel =
    item.badge === 'new' ? 'NEW' : item.badge === 'hot' ? 'TRENDING' : item.badge;

  const matchBadgeClass =
    item.matchLabel === 'Exact' ? styles.matchExact
      : item.matchLabel === 'Strong' ? styles.matchStrong
      : item.matchLabel === 'Moderate' ? styles.matchModerate
      : '';

  const matchedMetaText = item.matchedEntity
    ? `${item.matchedEntity.kind === 'person' ? 'Person' : 'Object'}: ${item.matchedEntity.label}`
    : item.matchedIn && item.matchedIn.length > 0
      ? item.matchedIn.join(' · ')
      : null;

  return (
    <div
      className={`${styles.tile} ${isActive && variant !== 'grid' ? styles.tileActive : ''} ${isSelected ? styles.tileSelected : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleTileClick}
      role="button"
      tabIndex={0}
      aria-label={`Open ${item.title}`}
      aria-pressed={isSelected}
      onKeyDown={(e) => e.key === 'Enter' && handleTileClick()}
    >
      <div className={styles.thumb}>
        {/* ── Background: thumbnail image or gradient placeholder ── */}
        {item.thumbnail ? (
          <img src={item.thumbnail} className={styles.thumbImg} alt={item.title} loading="lazy" />
        ) : (
          <>
            <div className={`${styles.thumbPlaceholder} ${styles[`grad_${item.gradient}`]}`} />
            <div className={styles.thumbIcon} aria-hidden="true">
              {item.contentType === 'image' ? (
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <circle cx="8" cy="10" r="1.6" fill="currentColor" stroke="none" />
                  <path d="M4 17l5-5 4 4 3-3 4 4" />
                </svg>
              ) : (
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polygon points="10,9 16,12 10,15" fill="currentColor" stroke="none" />
                </svg>
              )}
            </div>
          </>
        )}

        {/* ── Hover video preview ── */}
        <video
          ref={videoRef}
          className={`${styles.thumbVideo} ${isPortrait ? styles.thumbVideoPortrait : ''}`}
          src={item.storage_url ? undefined : demoVideo}
          muted
          loop={!item.storage_url}
          playsInline
          preload="none"
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            setIsPortrait(v.videoHeight > v.videoWidth);
          }}
        />

        {/* ── Gradient overlay + always-visible info (bottom of card) ── */}
        {variant !== 'grid' && (
          <div className={styles.bottomOverlay}>
            <div className={styles.bottomTitle}>{item.title}</div>
            <div className={styles.bottomMeta}>
              {item.creator && <span>{item.creator}</span>}
              {item.creator && item.timestamp && <span className={styles.metaDot}>·</span>}
              {item.timestamp && <span>{item.timestamp}</span>}
              {!item.creator && !item.timestamp && item.meta && <span>{item.meta}</span>}
              {matchedMetaText && (
                <>
                  {(item.creator || item.timestamp || item.meta) && <span className={styles.metaDot}>·</span>}
                  <span className={styles.matchedMeta}>{matchedMetaText}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Grid variant title overlay ── */}
        {variant === 'grid' && (
          <div className={styles.thumbTitleOverlay}>
            <span className={styles.thumbTitleText}>{item.title}</span>
          </div>
        )}

        {/* ── Search match badge (top-left, hidden on hover) — takes priority over category/legacy badges ── */}
        {item.matchLabel && !isSelected ? (
          <span className={`${styles.matchBadge} ${matchBadgeClass} ${styles.hiddenOnHover}`}>
            {item.matchLabel}
            {item.matchScore != null && <span className={styles.matchScore}>{Math.round(item.matchScore)}%</span>}
          </span>
        ) : item.category && !isSelected ? (
          <span className={`${styles.categoryBadge} ${styles[`cat_${item.category}`]} ${styles.hiddenOnHover}`}>
            {item.category.toUpperCase()}
          </span>
        ) : (
          !item.category && item.badge && !isSelected && (
            <span className={`${styles.badge} ${badgeClass} ${styles.hiddenOnHover}`}>{badgeLabel}</span>
          )
        )}

        {/* ── Duration badge (top-right) — hidden on hover only when this
            tile also has a remove button (same top-right corner), so the
            two don't overlap and the remove button stays reachable. ── */}
        {item.duration && (
          <span className={`${styles.duration} ${onRemove ? styles.hiddenOnHover : ''}`}>{item.duration}</span>
        )}

        {/* ── Image content-type icon (top-right, same slot as duration —
            images have none) — lets images be spotted at a glance in the grid. ── */}
        {isImage && (
          <span className={`${styles.imageTypeBadge} ${onRemove ? styles.hiddenOnHover : ''}`} aria-label="Image asset" title="Image">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </span>
        )}

        {/* ── Checkbox — top-left on hover or when selected (not on search result tiles) ── */}
        {!isSearchResult && (
          <button
            className={`${styles.checkbox} ${isSelected ? styles.checkboxSelected : ''}`}
            onClick={handleCheckbox}
            aria-label={isSelected ? 'Deselect clip' : 'Select clip'}
            aria-pressed={isSelected}
          >
            {isSelected ? (
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : null}
          </button>
        )}

        {/* ── Hover action buttons (center) — not on search result tiles.
            Play is video-only (images have no video to play); the tile click
            itself already opens details, so there's no separate "i" button. ── */}
        {!isSearchResult && (
          <div className={styles.hoverActions} aria-hidden="true">
            {!isImage && (
              <button className={styles.actionBtn} onClick={handlePlay} aria-label="Play video" title="Play">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
            <button
              className={`${styles.actionBtn} ${styles.actionBtnGreen}`}
              onClick={(e) => { e.stopPropagation(); trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_ADD_TO_COLLECTION, { assetId: item.id }); dispatch(openAssignModal(item.id)); }}
              aria-label="Add to collection"
              title="Add to Collection"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Hover dark overlay ── */}
        <div className={styles.hoverOverlay} />

        {/* ── Remove from collection button (top-right, shown on hover when onRemove provided) ── */}
        {onRemove && (
          <button
            className={styles.removeBtn}
            title="Remove from collection"
            onClick={(e) => { e.stopPropagation(); trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_REMOVE_FROM_COLLECTION, { assetId: item.id }); onRemove(); }}
            aria-label="Remove from collection"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            </svg>
          </button>
        )}

        {/* ── Delete asset button (top-right, shown on hover when onDelete provided) ── */}
        {onDelete && (
          <button
            className={`${styles.removeBtn} ${styles.deleteBtn}`}
            title="Delete video"
            onClick={(e) => { e.stopPropagation(); trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_DELETE, { assetId: item.id }); onDelete(); }}
            aria-label="Delete video"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        )}

        {/* ── Progress bar ── */}
        {item.progress !== undefined && (
          <div className={styles.progress}>
            <div className={styles.progressFill} style={{ width: `${item.progress}%` }} />
          </div>
        )}

        {/* ── Loading spinner ── */}
        {isLoading && (
          <div className={styles.loadingOverlay} aria-label="Loading">
            <div className={styles.spinner} />
          </div>
        )}

        {/* ── Active checkmark (playing) ── */}
        {isActive && variant !== 'grid' && !isSelected && (
          <div className={styles.activeOverlay} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
